import React from 'react';
import PropTypes from 'prop-types';
import { Checkbox } from '@gasbuddy/react-components';
import { Dropdown, DropdownToggle, DropdownMenu, DropdownItem } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';

import { StationListType } from '../../../lib/constants/enums';
import sharedStyles from '../../styles/shared.css';
import styles from './styles.css';

export default function StationListAccess({ stationLists: stationListProps, onChange, required, disabled, selectedListIds }) {
  const [selectedStationLists, setSelectedStationLists] = React.useState([]);
  const [stationLists, setStationLists] = React.useState([]);
  const [isOpen, setIsOpen] = React.useState(false);
  const [allListsSelected, setAllListsSelected] = React.useState(false);

  const stationListDropdown = React.useRef(null);

  React.useEffect(() => {
    window.addEventListener('mousedown', handleClickOutsideSelector);

    return () => {
      window.removeEventListener('mousedown', handleClickOutsideSelector);
    };
  }, []);

  React.useEffect(() => {
    onChange(selectedStationLists);
  }, [selectedStationLists.length]);

  React.useEffect(() => {
    setStationLists(stationListProps.filter(sl => !(sl.list_type === StationListType.AllStations || sl.list_type === StationListType.Watched)));
  }, [stationListProps]);

  React.useEffect(() => {
    setSelectedStationLists(
      stationListProps.filter(
        sl => selectedListIds.includes(sl.id) && !(sl.list_type === StationListType.AllStations || sl.list_type === StationListType.Watched),
      ),
    );
  }, [selectedListIds]);

  React.useEffect(() => {
    setAllListsSelected(stationLists.every(ssl => selectedStationLists.find(sl => sl.id === ssl.id)));
  }, [selectedStationLists.length, stationLists.length]);

  const closeDropdown = () => setIsOpen(false);

  const handleClickOutsideSelector = (event) => {
    if (stationListDropdown && !stationListDropdown?.current.getContainer().contains(event.target)) {
      closeDropdown();
    }
  };

  const handleToggle = () => {
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleOnCheckAllClick = (e) => {
    e.preventDefault();
    if (allListsSelected) {
      setSelectedStationLists([]);
    } else {
      setSelectedStationLists(stationLists);
    }
  };

  const handleOnStationListToggle = (stationList, e) => {
    if (selectedStationLists.find(ssl => ssl.id === stationList.id)) {
      setSelectedStationLists(selectedStationLists.filter(ssl => ssl.id !== stationList.id));
    } else {
      setSelectedStationLists([...selectedStationLists, stationList]);
    }
    e.preventDefault();
  };

  return (
    <div>
      <span className={styles.labelText}>
        Station Access
        {required ? <span className={styles.requiredInput}>*</span> : null}
      </span>
      <Dropdown
        className={`${sharedStyles.rsDropdown} ${styles.rsDropdownOverride}`}
        isOpen={isOpen}
        toggle={handleToggle}
        ref={stationListDropdown}
      >
        <DropdownToggle
          caret
          className={`${sharedStyles.rsDropdownToggle} ${styles.dropdownToggle}`}
          disabled={disabled}
        >
          <span className={styles.selectedStationLists}>
            {/* eslint-disable-next-line no-nested-ternary */}
            {selectedStationLists.length ? (
              allListsSelected ? 'All station lists selected' : selectedStationLists.map(sl => sl.name).join(', ')
            ) : 'No station lists checked'}
          </span>
          <FontAwesomeIcon className={sharedStyles.rsDropdownChevron} icon={isOpen ? faChevronUp : faChevronDown} />
        </DropdownToggle>
        <DropdownMenu className={`${sharedStyles.rsDropdownMenu} ${styles.dropdownMenu}`}>
          <DropdownItem
            key="selectAllToggle"
            onClick={handleOnCheckAllClick}
            className={styles.checkAllDropdownItem}
          >
            <Checkbox
              label={allListsSelected ? 'Uncheck All' : 'Check All'}
              className={`${styles.checkbox} ${styles.selectAllCheckbox}`}
              checked={allListsSelected}
            />
            <span className={styles.selectedStationCount}>{`${selectedStationLists.length} shared lists selected`}</span>
          </DropdownItem>
          <DropdownItem divider />
          {stationLists.map(sl => (
            <DropdownItem
              key={`stationList_${sl.id}`}
              onClick={e => handleOnStationListToggle(sl, e)}
            >
              <Checkbox
                label={`${sl.name} (${sl.station_count})`}
                className={styles.checkbox}
                checked={selectedStationLists.find(list => list.id === sl.id)}
              />
            </DropdownItem>
          ))}
        </DropdownMenu>
      </Dropdown>
    </div>
  );
}

StationListAccess.propTypes = {
  stationLists: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.number,
    name: PropTypes.string,
    list_type: PropTypes.string,
    station_count: PropTypes.number,
  })),
  onChange: PropTypes.func,
  required: PropTypes.bool,
  disabled: PropTypes.bool,
  selectedListIds: PropTypes.arrayOf(PropTypes.number),
};

StationListAccess.defaultProps = {
  stationLists: [],
  onChange: () => { },
  required: false,
  disabled: false,
  selectedListIds: [],
};
